import { Palette, User } from "lucide-react";
import React, { useEffect, useState } from "react";
import { createLogger } from "../../lib/logger";
import { useAppStore } from "../../store/useAppStore";
import { Avatar, AvatarFallback } from "../ui/avatar";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { ModeToggle } from "../ui/mode-toggle";
import { PageHeader } from "../ui/page-header";
import { Separator } from "../ui/separator";
import { ThemeSelector } from "../ui/theme-selector";

const log = createLogger("SettingsView");

function getInitials(name?: string, email?: string): string {
  const source = name?.trim() || email?.trim() || "";
  if (!source) return "U";
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return source.slice(0, 2).toUpperCase();
}

/**
 * A settings group: a heading and description, then its controls beneath.
 *
 * Stacked rather than the two-column label/content split this used to have —
 * that layout needs ~700px to breathe, and settings is now primarily a dialog
 * (see `SettingsDialog`), where it was cramping the labels onto three lines.
 */
function SettingsSection({
  icon: Icon,
  title,
  description,
  children,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-4 py-6">
      <div className="space-y-1">
        <h2 className="flex items-center gap-2 text-h5">
          <Icon className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          {title}
        </h2>
        <p className="text-xs leading-relaxed text-muted-foreground">
          {description}
        </p>
      </div>
      {children}
    </section>
  );
}

/**
 * Account settings.
 *
 * Only what the backend actually supports is here. The previous version also
 * offered password changes, two-factor setup, three notification toggles and a
 * billing plan — none of which had an API behind them, so every control was a
 * dead end that quietly did nothing. They were removed rather than disabled:
 * a disabled switch still advertises a feature that doesn't exist. Add each
 * section back alongside the endpoint that makes it real.
 *
 * Renders both at `/settings` and inside `SettingsDialog`; `inDialog` drops the
 * page header and width cap, which the dialog supplies itself.
 */
export function SettingsView({ inDialog = false }: { inDialog?: boolean } = {}) {
  const { user, fetchUserProfile, updateUserProfile } = useAppStore();

  const [isEditing, setIsEditing] = useState(false);
  const [editingName, setEditingName] = useState(user?.name || "");
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!user) {
      fetchUserProfile().catch((err) =>
        log.error("Failed to fetch user profile", err)
      );
    }
  }, [user, fetchUserProfile]);

  useEffect(() => {
    if (user) setEditingName(user.name || "");
  }, [user]);

  const handleSave = async () => {
    const trimmed = editingName.trim();
    if (trimmed && trimmed !== user?.name) {
      try {
        setIsSaving(true);
        await updateUserProfile({ name: trimmed });
      } catch (err) {
        log.error("Failed to update profile", err);
      } finally {
        setIsSaving(false);
      }
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditingName(user?.name || "");
    setIsEditing(false);
  };

  return (
    <div className={inDialog ? "space-y-0" : "max-w-2xl space-y-0"}>
      {!inDialog && (
        <>
          <PageHeader
            eyebrow="Account"
            title="Settings"
            description="Manage your account settings and preferences."
          />
          <div className="pt-2" />
        </>
      )}

      <SettingsSection
        icon={User}
        title="Profile"
        description="Your display name and account email."
      >
        <div className="space-y-6">
          <div className="flex items-center gap-4">
            <Avatar className="h-14 w-14">
              <AvatarFallback className="bg-accent text-base font-semibold text-accent-foreground">
                {getInitials(user?.name, user?.email)}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <p className="truncate text-sm font-medium">{user?.name || "—"}</p>
              <p className="truncate text-xs text-muted-foreground">
                {user?.email}
              </p>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="display-name">Display name</Label>
            {isEditing ? (
              <Input
                id="display-name"
                value={editingName}
                onChange={(e) => setEditingName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleSave();
                  if (e.key === "Escape") handleCancel();
                }}
                autoFocus
                className="max-w-sm"
              />
            ) : (
              <p className="text-sm text-muted-foreground">
                {user?.name || (
                  <span className="italic text-muted-foreground/50">Not set</span>
                )}
              </p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label>Email address</Label>
            <p className="text-sm text-muted-foreground">{user?.email || "—"}</p>
            <p className="text-2xs text-muted-foreground/60">
              Email address cannot be changed.
            </p>
          </div>

          {isEditing ? (
            <div className="flex gap-2">
              <Button size="sm" onClick={handleSave} disabled={isSaving}>
                {isSaving ? "Saving…" : "Save changes"}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={handleCancel}
                disabled={isSaving}
              >
                Cancel
              </Button>
            </div>
          ) : (
            <Button size="sm" variant="soft" onClick={() => setIsEditing(true)}>
              Edit profile
            </Button>
          )}
        </div>
      </SettingsSection>

      <Separator />

      <SettingsSection
        icon={Palette}
        title="Appearance"
        description="How Avahana looks on this device. Saved to this browser."
      >
        <div className="space-y-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="space-y-0.5">
              <p className="text-sm font-medium">Theme</p>
              <p className="text-xs text-muted-foreground">
                Light, dark, or follow your system.
              </p>
            </div>
            <ModeToggle />
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="space-y-0.5">
              <p className="text-sm font-medium">Accent colour</p>
              <p className="text-xs text-muted-foreground">
                Re-skins buttons, links and focus rings.
              </p>
            </div>
            <ThemeSelector />
          </div>
        </div>
      </SettingsSection>
    </div>
  );
}
